import { Icons } from '@/components/icon';
import { useSidePanelDispatch } from '@/components/sidepanel/side-panel-provider';
import { ActivityCell } from '@/components/table/cells/activity';
import { CompanyAvatar } from '@/components/table/cells/company';
import { PhoneCell, type PhoneCellHandle } from '@/components/table/cells/phone';
import { TeamCell, type TeamCellHandle } from '@/components/table/cells/team';
import type { FilterEvent } from '@/components/table/filter-bar';
import type {
  ColumnFilterConfig,
  FilterCondition,
  FilterValue,
} from '@/components/table/filter-chip';
import { ACTIVITY_CONDITIONS } from '@/components/table/filters/activity-filter-config';
import { ActivityValueInput } from '@/components/table/filters/activity-value-input';
import { SortButton, type SortColumn, type SortOrder } from '@/components/table/sort-button';
import { type ColumnDef, VirtualTable } from '@/components/table/table';
import type { ServerFilter } from '@/components/table/types';
import { mapConditionToOperator } from '@/components/table/utils';
import { Button, PageContent, PageHeader, PageLayout } from '@/components/ui/design-system';
import { P2, P3 } from '@/components/ui/text';
import { useServerFilter } from '@/hooks/use-server-filter';
import { useTranslation } from '@/hooks/use-translation';
import { useLocalizedDayjs } from '@/languages/dayjs';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo, $fetchAllo } from '@/services/api-allo/client';
import {
  type ContactFormData,
  CreateContactModal,
} from '@/views/contacts/components/create-contact-modal';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreateCompanyModal } from './components/create-company-modal';
import { useCompaniesQuery } from './queries/use-companies-query';
import {
  useCreateContactAndAddToCompanyMutation,
  useUpdateCompanyMembersMutation,
} from './queries/use-company-mutations';

type Company = components['schemas']['CompanyV2Response'];

interface UrlFilter {
  column: string;
  condition: FilterCondition;
  value?: string;
  serverField?: string;
}

export interface CompaniesSearchParams {
  filters?: string;
  sortField?: string;
  sortOrder?: SortOrder;
}

interface CompaniesViewProps {
  searchParams: CompaniesSearchParams;
  onNavigate: (updater: (prev: CompaniesSearchParams) => CompaniesSearchParams) => void;
}

export function parseUrlFilters(filtersParam: unknown): UrlFilter[] {
  if (typeof filtersParam !== 'string') {
    return [];
  }
  try {
    const parsed = JSON.parse(filtersParam);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (f): f is UrlFilter =>
        typeof f === 'object' &&
        f !== null &&
        typeof f.column === 'string' &&
        typeof f.condition === 'string',
    );
  } catch {
    return [];
  }
}

export function CompaniesView({ searchParams, onNavigate }: CompaniesViewProps) {
  const { t } = useTranslation('desktop_ui');
  const dayjs = useLocalizedDayjs();
  const setSidePanelState = useSidePanelDispatch();

  const phoneCellRefs = useRef<Map<string, PhoneCellHandle>>(new Map());
  const [openPhonePopoverCompanyId, setOpenPhonePopoverCompanyId] = useState<string | null>(null);
  const openPhonePopoverCompanyIdRef = useRef<string | null>(null);
  openPhonePopoverCompanyIdRef.current = openPhonePopoverCompanyId;

  const teamCellRefs = useRef<Map<string, TeamCellHandle>>(new Map());
  const [openTeamPopoverCompanyId, setOpenTeamPopoverCompanyId] = useState<string | null>(null);
  const openTeamPopoverCompanyIdRef = useRef<string | null>(null);
  openTeamPopoverCompanyIdRef.current = openTeamPopoverCompanyId;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false);
  const [createContactForCompanyId, setCreateContactForCompanyId] = useState<string | null>(null);

  // Create contact and add to company mutation
  const createContactAndAddMutation = useCreateContactAndAddToCompanyMutation({
    onSuccess: () => {
      setIsCreateContactModalOpen(false);
      setCreateContactForCompanyId(null);
    },
  });

  const handleCreateContactForCompany = useCallback((companyId: string) => {
    setCreateContactForCompanyId(companyId);
    setIsCreateContactModalOpen(true);
  }, []);

  const handleCreateContactSubmit = useCallback(
    (data: ContactFormData) => {
      if (!createContactForCompanyId) {
        return;
      }
      createContactAndAddMutation.createContactAndAddToCompany({
        companyId: createContactForCompanyId,
        ...data,
      });
    },
    [createContactForCompanyId, createContactAndAddMutation],
  );

  // Prefetch contacts for the team popover to avoid loading state
  const queryClient = useQueryClient();
  useEffect(() => {
    const queryOptions = $apiAllo.queryOptions('post', '/v2/contact/filter', {
      body: {
        filter: { and: [] },
        sort: [{ field: 'last_activity_date', order: 'desc' }],
        pagination: { page: 0, size: 10 },
      },
    });
    queryClient.prefetchQuery({ ...queryOptions, staleTime: 5000 });
  }, [queryClient]);

  // TODO: Implement company phone add/delete when API is available
  const handlePhoneAdd = useCallback(
    (_companyId: string, _currentNumbers: string[], _phone: string) => {
      // updateCompany(companyId, { phone_numbers: [...currentNumbers, phone] });
    },
    [],
  );

  const handlePhoneDelete = useCallback(
    (_companyId: string, _currentNumbers: string[], _phone: string) => {
      // updateCompany(companyId, { phone_numbers: currentNumbers.filter((n) => n !== phone) });
    },
    [],
  );

  const { addContacts, removeContacts } = useUpdateCompanyMembersMutation();

  // Track current company members for change detection
  const companiesRef = useRef<Company[]>([]);

  const handleMembersChange = useCallback(
    (
      companyId: string,
      newMemberIds: string[],
      addedContact?: { id: string; name: string; last_name: string },
    ) => {
      // Find the current company to get its members
      const company = companiesRef.current.find((c) => c.id === companyId);
      const currentMemberIds = (company?.members ?? [])
        .map((m) => m.id)
        .filter((id): id is string => !!id);

      // Find contacts to add (in newMemberIds but not in currentMemberIds)
      const contactsToAdd = newMemberIds.filter((id) => !currentMemberIds.includes(id));

      // Find contacts to remove (in currentMemberIds but not in newMemberIds)
      const contactsToRemove = currentMemberIds.filter((id) => !newMemberIds.includes(id));

      if (contactsToAdd.length > 0) {
        // Pass contact info for optimistic updates
        const contactsInfo = addedContact ? [addedContact] : [];
        addContacts(companyId, contactsToAdd, contactsInfo);
      }

      if (contactsToRemove.length > 0) {
        removeContacts(companyId, contactsToRemove);
      }
    },
    [addContacts, removeContacts],
  );

  const filterableColumns = useMemo(
    () => [
      {
        value: 'name',
        label: t('components.companies.filter.columns.name'),
        icon: 'BuildingOffice' as const,
      },
      {
        value: 'phone_number',
        label: t('components.companies.filter.columns.phone_number'),
        icon: 'Phone' as const,
      },
      {
        value: 'member',
        label: t('components.companies.filter.columns.member'),
        icon: 'User' as const,
      },
      {
        value: 'activity',
        label: t('components.companies.filter.columns.activity'),
        icon: 'Pulse' as const,
      },
    ],
    [t],
  );

  const columnConfigs = useMemo<Record<string, ColumnFilterConfig>>(
    () => ({
      activity: {
        customConditions: [...ACTIVITY_CONDITIONS],
        renderValueInput: (props) => <ActivityValueInput {...props} />,
      },
    }),
    [],
  );

  const sortableColumns: SortColumn[] = useMemo(
    () => [
      {
        value: 'name',
        label: t('components.companies.filter.columns.name'),
        icon: 'BuildingOffice',
      },
      {
        value: 'last_activity_date',
        label: t('components.companies.table.columns.lastActivity'),
        icon: 'Pulse',
      },
      {
        value: 'created_at',
        label: t('components.companies.table.columns.createdAt'),
        icon: 'Calendar',
      },
      {
        value: 'updated_at',
        label: t('components.companies.table.columns.updatedAt'),
        icon: 'Calendar',
      },
    ],
    [t],
  );

  const sortField = searchParams.sortField ?? 'last_activity_date';
  const sortOrder = searchParams.sortOrder ?? 'desc';
  const isCustomSort = sortField !== 'last_activity_date' || sortOrder !== 'desc';

  const urlFilters = useMemo(() => parseUrlFilters(searchParams.filters), [searchParams.filters]);

  const initialFilters = useMemo((): FilterValue[] => {
    const result: FilterValue[] = [];
    for (const f of urlFilters) {
      const column = filterableColumns.find((c) => c.value === f.column);
      if (column) {
        result.push({
          id: `url-filter-${f.column}-${f.condition}-${f.value ?? ''}`,
          columnId: f.column,
          columnLabel: column.label,
          columnIcon: column.icon,
          condition: f.condition,
          value: f.value,
          serverField: f.serverField,
        });
      }
    }
    return result;
  }, [urlFilters, filterableColumns]);

  const initialServerFilter = useMemo((): ServerFilter | null => {
    if (urlFilters.length === 0) {
      return isCustomSort ? { and: [] } : null;
    }

    const conditionsWithoutValue = ['empty', 'not_empty'];

    const validFilters = urlFilters.filter((f) => {
      if (!conditionsWithoutValue.includes(f.condition)) {
        return f.value && f.value.trim() !== '';
      }
      return true;
    });

    if (validFilters.length === 0) {
      return isCustomSort ? { and: [] } : null;
    }

    const conditions = validFilters.map((f) => ({
      field: f.serverField || f.column,
      operator: mapConditionToOperator(f.condition),
      value: f.value ?? '',
    }));

    return { and: conditions };
  }, [urlFilters, isCustomSort]);

  type FilterResponse = {
    data: Company[];
    metadata?: {
      pagination?: { current_page?: number; total_pages?: number };
    };
  };

  const serverFilter = useServerFilter<Company, FilterResponse>({
    queryKey: ['companies', 'filter', sortField, sortOrder],
    queryFn: async ({ filter, pagination }) => {
      const response = await $fetchAllo.POST('/v1/company/filter', {
        body: {
          filter: {
            and: filter.and.map((condition) => ({
              field: condition.field as
                | 'p_id'
                | 'name'
                | 'member'
                | 'phone_number'
                | 'event_type'
                | 'event_user_account'
                | 'event_created_at'
                | 'created_at'
                | 'updated_at'
                | 'last_activity',
              operator: condition.operator as
                | 'starts_with'
                | 'ends_with'
                | 'contains'
                | 'not_contains'
                | 'equals'
                | 'not_equals'
                | 'empty'
                | 'not_empty'
                | 'greater'
                | 'less',
              value: String(condition.value ?? ''),
            })),
          },
          sort: [
            {
              field: sortField as 'name' | 'created_at' | 'updated_at' | 'last_activity',
              order: sortOrder,
            },
          ],
          pagination: { page: pagination.page, size: pagination.pageSize },
        },
      });
      const result = response.data;
      return {
        data: (result?.data as Company[]) ?? [],
        metadata: result?.metadata,
      };
    },
    select: (response) => response.data,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.metadata?.pagination;
      if (!pagination) {
        return undefined;
      }
      const currentPage = pagination.current_page ?? 0;
      const totalPages = pagination.total_pages ?? 0;
      return currentPage < totalPages - 1 ? currentPage + 1 : undefined;
    },
    initialFilter: initialServerFilter,
    pageSize: 50,
    forceEnabled: isCustomSort,
  });

  const handleServerFilter = useCallback(
    (filter: ServerFilter, originalFilters: FilterValue[]) => {
      serverFilter.applyFilter(filter);

      // Use original filters to preserve columnId and condition for URL
      const newFilters: UrlFilter[] = originalFilters
        .filter((f) => f.condition !== null)
        .map((f) => ({
          column: f.columnId,
          condition: f.condition as FilterCondition,
          value: f.value,
          serverField: f.serverField,
        }));

      if (newFilters.length > 0) {
        onNavigate((prev) => ({
          ...prev,
          filters: JSON.stringify(newFilters),
        }));
      } else {
        onNavigate((prev) => ({
          sortField: prev.sortField,
          sortOrder: prev.sortOrder,
        }));
      }
    },
    [onNavigate, serverFilter.applyFilter],
  );

  const handleFilterEvent = useCallback((event: FilterEvent) => {
    const eventName =
      event.type === 'applied'
        ? ANALYTICS_EVENTS.FILTERS.APPLIED
        : ANALYTICS_EVENTS.FILTERS.REMOVED;
    analytics.track(eventName, {
      page: 'companies',
      filter_column: event.filter.columnId,
      filter_condition: event.filter.condition,
      filter_value: event.filter.value,
      active_filters: event.activeFilters.map((f) => f.columnId),
      active_filter_count: event.activeFilters.length,
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    analytics.track(ANALYTICS_EVENTS.FILTERS.CLEARED, { page: 'companies' });
    serverFilter.clearFilter();
    onNavigate((prev) => ({
      sortField: prev.sortField,
      sortOrder: prev.sortOrder,
    }));
  }, [onNavigate, serverFilter.clearFilter]);

  const handleSortChange = useCallback(
    (newField: string, newOrder: SortOrder) => {
      analytics.track(ANALYTICS_EVENTS.COMPANIES.SORT_CHANGED, {
        sort_field: newField,
        sort_order: newOrder,
      });
      onNavigate((prev) => ({
        ...prev,
        sortField: newField,
        sortOrder: newOrder,
      }));
    },
    [onNavigate],
  );

  const companyQuery = useCompaniesQuery({ enabled: !serverFilter.isActive });

  const showLoading = serverFilter.isActive
    ? serverFilter.isLoading || (serverFilter.isFetching && serverFilter.isPlaceholderData)
    : companyQuery.isLoading;

  const companies = useMemo(() => {
    if (serverFilter.isActive) {
      const seen = new Set<string>();
      return serverFilter.data.filter((company) => {
        if (!company.id || seen.has(company.id)) {
          return false;
        }
        seen.add(company.id);
        return true;
      });
    }

    if (!companyQuery.data?.pages) {
      return [];
    }
    const allCompanies = companyQuery.data.pages.flatMap((page) => (page?.data as Company[]) ?? []);
    const seen = new Set<string>();
    return allCompanies.filter((company) => {
      if (!company.id || seen.has(company.id)) {
        return false;
      }
      seen.add(company.id);
      return true;
    });
  }, [serverFilter.isActive, serverFilter.data, companyQuery.data?.pages]);

  // Keep companiesRef in sync for optimistic updates
  useEffect(() => {
    companiesRef.current = companies;
  }, [companies]);

  const handleCompanyClick = useCallback(
    (
      companyId: string | undefined,
      companyName: string | undefined,
      companyImageUrl?: string | null,
    ) => {
      if (!companyId) {
        return;
      }
      analytics.track(ANALYTICS_EVENTS.COMPANIES.VIEWED, {
        company_id: companyId,
        company_name: companyName,
      });
      setSidePanelState({
        open: true,
        mode: 'companies',
        context: { companyId },
        entityInfo: { name: companyName || 'Company', imageUrl: companyImageUrl ?? undefined },
      });

      const currentIndex = companies.findIndex((c) => c.id === companyId);
      if (currentIndex !== -1) {
        const nextCompanies = companies.slice(currentIndex + 1, currentIndex + 6);
        for (const company of nextCompanies) {
          if (company.id) {
            queryClient.prefetchQuery(
              $apiAllo.queryOptions('post', '/v1/company/filter', {
                body: {
                  filter: {
                    and: [
                      {
                        field: 'p_id' as 'name',
                        operator: 'equals' as const,
                        value: company.id,
                      },
                    ],
                  },
                  pagination: { page: 0, size: 1 },
                },
              }),
            );
          }
        }
      }
    },
    [setSidePanelState, companies, queryClient],
  );

  const handleTeamMemberClick = useCallback(
    (memberId: string, members?: Company['members']) => {
      setSidePanelState({
        open: true,
        mode: 'contacts',
        context: { contactId: memberId },
      });

      // Prefetch other members from the same company
      if (members) {
        for (const member of members) {
          if (member.id && member.id !== memberId) {
            queryClient.prefetchQuery(
              $apiAllo.queryOptions('get', '/v1/contact/{contact_id}', {
                params: {
                  path: { contact_id: member.id },
                },
              }),
            );
          }
        }
      }
    },
    [setSidePanelState, queryClient],
  );

  const columns: ColumnDef<Company, unknown>[] = useMemo(
    () => [
      {
        id: 'name',
        accessorFn: (row) => row.name ?? '',
        cell: ({ row }) => {
          const company = row.original;
          const name = company.name ?? '';

          return (
            <button
              type="button"
              onClick={() => handleCompanyClick(company.id, name, company.image_url)}
              className="h-full w-full text-left text-primary-100 flex items-center gap-2 cursor-pointer"
            >
              <CompanyAvatar imageUrl={company.image_url} size={20} />
              <P2 className="truncate">{name || t('components.companies.table.unknown')}</P2>
            </button>
          );
        },
        header: t('components.companies.table.columns.name'),
        meta: { icon: 'BuildingOffice' },
        size: 250,
        minSize: 200,
      },
      {
        id: 'team',
        accessorFn: (row) => row.members?.length ?? 0,
        cell: ({ row }) => {
          const companyId = row.original.id;
          return (
            <TeamCell
              ref={(handle) => {
                if (companyId) {
                  if (handle) {
                    teamCellRefs.current.set(companyId, handle);
                  } else {
                    teamCellRefs.current.delete(companyId);
                  }
                }
              }}
              companyId={companyId}
              members={row.original.members}
              isOpen={openTeamPopoverCompanyIdRef.current === companyId}
              onOpenChange={(open) =>
                setOpenTeamPopoverCompanyId(open ? (companyId ?? null) : null)
              }
              onMembersChange={handleMembersChange}
              onCreateContact={handleCreateContactForCompany}
              onMemberClick={(memberId) => handleTeamMemberClick(memberId, row.original.members)}
            />
          );
        },
        header: t('components.companies.table.columns.team'),
        meta: { icon: 'Teams', skipDefaultCell: true, elevateOnFocus: true },
        size: 180,
        minSize: 120,
      },
      {
        id: 'phone',
        accessorFn: (row) => row.phone_numbers?.join(', ') ?? '',
        header: t('components.companies.table.columns.phone'),
        meta: {
          icon: 'Phone',
          skipDefaultCell: true,
          elevateOnFocus: true,
        },
        cell: ({ row }) => {
          const companyId = row.original.id;
          const phoneNumbers = row.original.phone_numbers ?? [];
          return (
            <PhoneCell
              ref={(handle) => {
                if (companyId) {
                  if (handle) {
                    phoneCellRefs.current.set(companyId, handle);
                  } else {
                    phoneCellRefs.current.delete(companyId);
                  }
                }
              }}
              contactId={companyId}
              phoneNumbers={phoneNumbers}
              isOpen={false}
              onOpenChange={(open) =>
                setOpenPhonePopoverCompanyId(open ? (companyId ?? null) : null)
              }
              onPhoneAdd={handlePhoneAdd}
              onPhoneDelete={handlePhoneDelete}
            />
          );
        },
        size: 200,
        minSize: 150,
      },
      {
        id: 'activity',
        accessorFn: (row) => row.activity?.[0]?.type ?? '',
        cell: ({ row }) => <ActivityCell activity={row.original.activity?.[0]} />,
        header: t('components.companies.table.columns.lastActivity'),
        meta: { icon: 'Pulse', disabled: true },
        size: 180,
        minSize: 120,
      },
      {
        id: 'last_activity_date',
        accessorFn: (row) => row.last_activity_date ?? '',
        cell: ({ row }) => {
          if (!row.original.last_activity_date) {
            return null;
          }
          const formatted = dayjs(row.original.last_activity_date).format('MMM Do YYYY, h:mma');
          return (
            <P3 className="text-primary-60">
              {formatted.replace(/\s+(am|pm)/i, (_match, period) => period.toLowerCase())}
            </P3>
          );
        },
        header: t('components.companies.table.columns.updatedAt'),
        meta: { icon: 'Calendar', disabled: true },
        size: 200,
        minSize: 150,
      },
      {
        id: 'created_at',
        accessorFn: (row) => row.created_at ?? '',
        cell: ({ row }) => {
          if (!row.original.created_at) {
            return null;
          }
          const formatted = dayjs(row.original.created_at).format('MMM Do YYYY, h:mma');
          return (
            <P3 className="text-primary-60">
              {formatted.replace(/\s+(am|pm)/i, (_match, period) => period.toLowerCase())}
            </P3>
          );
        },
        header: t('components.companies.table.columns.createdAt'),
        meta: { icon: 'Calendar', disabled: true },
        size: 200,
        minSize: 150,
      },
    ],
    [
      dayjs,
      t,
      handleCompanyClick,
      handlePhoneAdd,
      handlePhoneDelete,
      handleMembersChange,
      handleCreateContactForCompany,
    ],
  );

  return (
    <>
      <CreateCompanyModal open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} />
      <CreateContactModal
        open={isCreateContactModalOpen}
        onOpenChange={setIsCreateContactModalOpen}
        onCustomSubmit={handleCreateContactSubmit}
        isCustomSubmitPending={createContactAndAddMutation.isPending}
      />
      <PageLayout>
        <PageHeader
          title={t('components.companies.name')}
          rightAddOn={
            <Button
              variant="outline"
              size="sm"
              iconName={Icons.Plus}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t('components.companies.table.createCompany')}
            </Button>
          }
        />
        <PageContent>
          <VirtualTable
            data={companies}
            columns={columns}
            height="calc(100vh - 70px)"
            enableSorting
            enableColumnResizing
            columnPinning={{ left: ['name'] }}
            tableControl={
              <SortButton
                field={sortField}
                order={sortOrder}
                onSortChange={handleSortChange}
                columns={sortableColumns}
              />
            }
            filterableColumns={filterableColumns}
            columnConfigs={columnConfigs}
            manualFiltering
            onServerFilter={handleServerFilter}
            onFilterEvent={handleFilterEvent}
            initialFilters={initialFilters}
            hasActiveFilters={(serverFilter.filter?.and.length ?? 0) > 0}
            onClearFilters={handleClearFilters}
            isLoading={showLoading}
            getRowId={(row) => row.id ?? ''}
            onLoadMore={
              serverFilter.isActive ? serverFilter.fetchNextPage : companyQuery.fetchNextPage
            }
            hasNextPage={
              serverFilter.isActive ? serverFilter.hasNextPage : companyQuery.hasNextPage
            }
            isFetchingNextPage={
              serverFilter.isActive
                ? serverFilter.isFetchingNextPage
                : companyQuery.isFetchingNextPage
            }
            overscan={20}
            onCellActivate={(_rowIndex, columnIndex, row) => {
              const columnId = columns[columnIndex]?.id;
              if (columnId === 'name') {
                handleCompanyClick(
                  row.original.id,
                  row.original.name ?? undefined,
                  row.original.image_url,
                );
              } else if (columnId === 'phone' && row.original.id) {
                phoneCellRefs.current.get(row.original.id)?.open();
              } else if (columnId === 'team' && row.original.id) {
                teamCellRefs.current.get(row.original.id)?.open();
              }
            }}
          />
        </PageContent>
      </PageLayout>
    </>
  );
}
